import { useContext, useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { normalizeForSearch } from '../../../utils/fuzzy'
import {
    ActionDependant,
    GlobalDataContext,
    TemplateContext,
    dataLocationToPath,
    evaluateTemplateValue,
    evaluateTemplateValueCollection,
    propsDataLocationToPathAndValue,
} from '@ea-lab/reactive-json'

/**
 * Champ de saisie LIBRE de tags (free tagging), générique.
 *
 * POURQUOI DU JSX (justification config-first) : le geste « free tagging » est un
 * *retrouver-ou-créer* — à la validation d'un libellé il faut chercher une
 * correspondance insensible à la casse dans les options chargées puis, SEULEMENT
 * si aucune ne correspond, POSTer la création du terme et pousser l'id renvoyé
 * dans la liste. reactive-json ne sait ni comparer une saisie à une collection,
 * ni conditionner un `submitData` sur le résultat de cette comparaison : sans ce
 * branchement, ou bien on crée un doublon à chaque frappe, ou bien on ne crée
 * jamais rien. Le composant est écrit UNE fois pour tout champ de taxonomie à
 * saisie libre (il ne connaît ni entité, ni vocabulaire, ni domaine).
 *
 * Transport : le patron « Syncable Object » de decoupled_toolbox
 * (`{submission_url, data, status}` POSTé tel quel), identique à
 * `CreatableSelectField` — l'id créé est à la RACINE de la réponse.
 *
 * Commit : le composant POSTe lui-même le champ sur le `submission_url` reçu (POST
 * PARTIEL — le back n'écrit que les clés reçues). Ce n'est pas un choix de confort :
 * `submitData` n'évalue son `data:` que sur UN niveau (cf. dist submitData.js), donc
 * un corps `{data: {tags: ~.data.tags}}` partirait avec la chaîne littérale ; et
 * scinder en `setData` puis `submitData` sur le même clic ferait lire au POST l'état
 * d'AVANT (React n'a pas re-rendu). L'écriture est optimiste et REVERTÉE si le
 * serveur refuse. Comme `SearchField`, le composant clique en plus un bouton caché,
 * seul enfant d'`ActionDependant`, pour que le YAML puisse réagir au commit.
 *
 * Usage YAML :
 *   - type: CreatableTagsField
 *     dataLocation: ~.data.tags              # tableau d'ids (TID)
 *     dynamicOptions: ~~.tagVocab.options    # [{value, label}]
 *     optionsPath: ~~.tagVocab.options       # où pousser une option créée
 *     labelsPath: ~~.tagVocab.labels         # (option.) table { value: libellé }
 *     createSubmissionUrl: "/decoupled-api/save?display=decoupled&entity_type=taxonomy_term&bundle=dashboard_tag"
 *     createFieldName: name                  # défaut : name
 *     createData: { siteIds: ~~.id }         # (option.) champs fixes du terme créé
 *     submissionUrl: ~.submission_url        # où persister le champ (voyage avec l'item)
 *     submissionKey: tags                    # clé decoupled du champ (défaut : tags)
 *     placeholder: "+ tag"
 *     ariaLabel: "Ajouter un tag"
 *     editable: ~.canEdit                    # false = badges en lecture seule
 *     actions:                               # (option.) jouées après chaque commit
 *       - { what: setData, on: click, path: ~~.notice, value: "Tags enregistrés." }
 *
 * URL D'ÉCRITURE FABRIQUÉE — l'exception, et pourquoi. Le patron maison veut que
 * les URLs voyagent AVEC la donnée (`submission_url` du Syncable Object), et
 * c'est ce que fait l'usage ci-dessus. Mais une ligne servie par Elasticsearch
 * est un `_source` à plat : elle ne transporte rien de tel, et l'y indexer
 * serait pire — une URL d'écriture indexée serait la même pour tout le monde,
 * alors que decoupled_toolbox n'émet la vraie qu'aux comptes qui peuvent
 * écrire. Pour ce cas-là, `submissionUrl` et `createSubmissionUrl` acceptent
 * aussi la forme `{template, values}` de `PathLink` :
 *
 *     submissionUrl:
 *       template: "/decoupled-api/save?display=decoupled&entity_type=node&bundle=:bundle&entity_id=:id"
 *       values: { bundle: ~.type, id: ~.id }
 *
 * Le gabarit est déclaré dans le YAML (donc du contenu), les valeurs viennent du
 * store, et chacune est URL-encodée. Une URL ainsi fabriquée n'est conditionnée
 * à AUCUN droit — c'est le back qui refuse, avec son message, exactement comme
 * les boutons « Modifier » d'une liste servie par Elasticsearch.
 */
export const CreatableTagsField = ({ props, path, datafield }) => {
    const globalDataContext = useContext(GlobalDataContext)
    const templateContext = useContext(TemplateContext)
    const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

    const [draft, setDraft] = useState('')
    const [isSaving, setIsSaving] = useState(false)
    const [error, setError] = useState(null)
    const triggerRef = useRef(null)
    const inputRef = useRef(null)
    // Le commit doit lire le store APRÈS son écriture : on l'arme ici et on le
    // déclenche dans l'effet qui suit le rendu porteur de la nouvelle valeur.
    const commitPending = useRef(false)

    const { formData, formDataPath } = propsDataLocationToPathAndValue({
        currentPath: path,
        datafield,
        dataLocation: props.dataLocation,
        defaultValue: [],
        globalDataContext,
        templateContext,
    })

    /**
     * Une prop de texte : soit une chaîne (résolue comme n'importe quelle
     * valeur), soit un `{template, values}` interpolé à la façon de `PathLink`
     * — reactive-json ne concatène pas une chaîne dans une prop, et il faut
     * bien fabriquer l'URL d'écriture d'une ligne qui ne transporte pas son
     * `submission_url`, comme le nom accessible d'un champ répété par ligne.
     *
     * `encode` : les segments d'une URL sont URL-encodés, un libellé non.
     */
    const resolveTemplated = (prop, { encode = false } = {}) => {
        if (!prop) return ''
        if (typeof prop !== 'object' || Array.isArray(prop) || prop.template === undefined) {
            return resolve(prop) || ''
        }
        const template = resolve(prop.template) || ''
        const values = evaluateTemplateValueCollection({
            valueToEvaluate: prop.values ?? {},
            globalDataContext,
            templateContext,
        })
        // Un jeton dont la valeur manque reste VISIBLE (`:id`) : une requête qui
        // part vers un chemin manifestement faux se repère, là où un
        // `entity_id=` vide créerait silencieusement une entité.
        return template.replace(/:([a-zA-Z0-9_]+)/g, (token, key) => {
            const value = values?.[key]
            if (value === undefined || value === null || value === '') return token
            return encode ? encodeURIComponent(String(value)) : String(value)
        })
    }
    const resolveUrl = (prop) => resolveTemplated(prop, { encode: true })

    const options = resolve(props.dynamicOptions) || []
    // Un champ multivalué n'arrive PAS toujours en tableau : le rendu `raw` de
    // l'endpoint Elasticsearch aplatit une valeur unique en scalaire
    // (`tags_tid: 132` et non `[132]`), si bien qu'une ligne à une seule
    // catégorie s'affichait sans aucun badge — et sans la moindre erreur. On
    // normalise donc ici, une fois, plutôt que de parier sur la forme reçue.
    const tags = Array.isArray(formData)
        ? formData.map(String)
        : (formData === undefined || formData === null || formData === '' ? [] : [String(formData)])
    const placeholder = resolve(props.placeholder) ?? '+ tag'
    const ariaLabel = resolveTemplated(props.ariaLabel) || placeholder
    const createUrl = resolveUrl(props.createSubmissionUrl)
    const createFieldName = resolve(props.createFieldName) || 'name'
    // Champs FIXES ajoutés au corps de création, à côté du libellé saisi : le
    // terme naît avec ses valeurs (le site auquel appartient la catégorie, par
    // exemple) plutôt que par une seconde écriture. Évalué sur UN niveau, comme
    // le `values` d'un gabarit d'URL.
    const createData = evaluateTemplateValueCollection({
        valueToEvaluate: props.createData ?? {},
        globalDataContext,
        templateContext,
    })
    const submissionUrl = resolveUrl(props.submissionUrl)
    const submissionKey = resolve(props.submissionKey) || 'tags'
    const editable = resolve(props.editable) !== false

    // Chemin de store d'une prop « où écrire » (options / libellés), résolu comme
    // le fait CreatableSelectField : reactive-json rend un chemin préfixé `data.`.
    const storePathOf = (dataLocation) => {
        if (!dataLocation) return null
        try {
            const raw = dataLocationToPath({
                dataLocation,
                currentPath: templateContext.templatePath,
                globalDataContext,
                templateContext,
            })
            return raw?.startsWith('data.') ? raw.substring(5) : raw === 'data' ? '' : raw
        } catch { return null }
    }

    const labelOf = (value) =>
        options.find((o) => String(o.value) === String(value))?.label ?? String(value)

    const findByLabel = (label) =>
        options.find((o) => normalizeForSearch(o.label) === normalizeForSearch(label))

    /** Écriture optimiste + POST partiel ; retour arrière si le serveur refuse. */
    const writeTags = async (next) => {
        const previous = tags
        commitPending.current = true
        globalDataContext.updateData(next, formDataPath)
        if (!submissionUrl) return

        setIsSaving(true)
        setError(null)
        try {
            const response = await fetch(submissionUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    submission_url: submissionUrl,
                    data: { [submissionKey]: next },
                    status: null,
                }),
            })
            if (!response.ok) {
                const body = await response.json().catch(() => null)
                globalDataContext.updateData(previous, formDataPath)
                setError(body?.status?.message || "Enregistrement des tags refusé.")
            }
        } catch {
            globalDataContext.updateData(previous, formDataPath)
            setError('Erreur réseau : tags non enregistrés.')
        } finally {
            setIsSaving(false)
        }
    }

    useEffect(() => {
        if (!commitPending.current) return
        commitPending.current = false
        triggerRef.current?.click()
    }, [formData])

    const addValue = (value) => {
        const v = String(value)
        if (tags.includes(v)) return
        writeTags([...tags, v])
    }

    const removeValue = (value) => {
        writeTags(tags.filter((t) => String(t) !== String(value)))
    }

    /** Retrouver-ou-créer : le cœur du free tagging. */
    const commitDraft = async () => {
        const label = draft.trim()
        if (!label || isSaving) return
        setError(null)

        const known = findByLabel(label)
        if (known) {
            setDraft('')
            addValue(known.value)
            return
        }
        if (!createUrl) {
            setError('Tag inconnu.')
            return
        }

        setIsSaving(true)
        try {
            const response = await fetch(createUrl, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    submission_url: createUrl,
                    data: { [createFieldName]: label, ...createData },
                    status: null,
                }),
            })
            const result = await response.json().catch(() => null)
            const newId = String(result?.data?.id ?? result?.id ?? '')
            if (!response.ok || !newId) {
                setError(result?.status?.message || "Impossible de créer ce tag.")
                return
            }

            // Le nouveau terme rejoint le vocabulaire chargé : les autres lignes
            // (et la facette) le connaissent sans recharger la page.
            const optionsStorePath = storePathOf(props.optionsPath)
            if (optionsStorePath !== null) {
                globalDataContext.updateData([...options, { value: newId, label }], 'data.' + optionsStorePath)
            }
            const labelsStorePath = storePathOf(props.labelsPath)
            if (labelsStorePath !== null) {
                const labels = resolve(props.labelsPath) || {}
                globalDataContext.updateData({ ...labels, [newId]: label }, 'data.' + labelsStorePath)
            }

            setDraft('')
            addValue(newId)
        } catch {
            setError('Erreur réseau lors de la création du tag.')
        } finally {
            setIsSaving(false)
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            commitDraft()
            return
        }
        // Retour arrière sur un champ vide : retirer le dernier tag (raccourci
        // usuel des champs à jetons).
        if (e.key === 'Backspace' && draft === '' && tags.length) {
            e.preventDefault()
            removeValue(tags[tags.length - 1])
        }
    }

    const datalistId = `tags-${String(formDataPath).replace(/[^a-zA-Z0-9]/g, '-')}`

    return (
        <>
            <div className="flex flex-wrap items-center gap-1.5">
                {tags.map((value) => (
                    <span key={value} className="badge badge-primary badge-sm">
                        {labelOf(value)}
                        {editable && (
                            <button
                                type="button"
                                className="inline-flex rounded-full hover:text-iris-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-iris-500"
                                onClick={() => removeValue(value)}
                                title={`Retirer le tag ${labelOf(value)}`}
                                aria-label={`Retirer le tag ${labelOf(value)}`}
                            >
                                <X size={11} aria-hidden="true" />
                            </button>
                        )}
                    </span>
                ))}

                {editable && (
                    <>
                        <input
                            ref={inputRef}
                            type="text"
                            className="input input-sm w-28"
                            value={draft}
                            list={datalistId}
                            disabled={isSaving}
                            placeholder={placeholder}
                            aria-label={ariaLabel}
                            onChange={(e) => {
                                setDraft(e.target.value)
                                // Choix dans la datalist : la valeur arrive complète
                                // d'un coup — on valide sans attendre Entrée.
                                if (findByLabel(e.target.value)) {
                                    setDraft('')
                                    addValue(findByLabel(e.target.value).value)
                                }
                            }}
                            onKeyDown={handleKeyDown}
                            onBlur={commitDraft}
                        />
                        <datalist id={datalistId}>
                            {options
                                .filter((o) => !tags.includes(String(o.value)))
                                .map((o) => <option key={o.value} value={o.label} />)}
                        </datalist>
                    </>
                )}
            </div>

            {error && <p className="text-xs text-rose-600 mt-1">{error}</p>}

            {/* Déclencheur isolé : SEUL enfant d'ActionDependant, cliqué par le
                composant — les `actions:` du YAML font la persistance. */}
            <ActionDependant {...props} attributesHolderRef={triggerRef}>
                <button ref={triggerRef} type="button" className="hidden" aria-hidden="true" tabIndex={-1} />
            </ActionDependant>
        </>
    )
}
